import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AddUserModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    mfaEnabled: false,
    sendWelcomeEmail: true,
  });

  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'Admin', label: 'Admin - Full System Access' },
    { value: 'Ops', label: 'Operations - Shipment Management' },
    { value: 'Finance', label: 'Finance - Cost & Fee Access' },
    { value: 'RNR', label: 'RNR - Limited Read Access' },
    { value: 'Business Manager', label: 'Business Manager - Invoice Approval & Financial Oversight' },
    { value: 'Logistics Coordinator', label: 'Logistics Coordinator - Trading & Operations' },
    { value: 'Finance Analyst', label: 'Finance Analyst - Trading & Selected Financial Management' },
  ];

  const departmentOptions = [
    { value: 'Operations', label: 'Operations' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Administration', label: 'Administration' },
    { value: 'Trading', label: 'Trading' },
    { value: 'Management', label: 'Management' },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onAdd(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      role: '',
      department: '',
      mfaEnabled: false,
      sendWelcomeEmail: true,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative bg-card rounded-lg shadow-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Add New User
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Create a new user account with role-based permissions
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent/10 hover:text-accent transition-colors"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter full name"
              value={formData?.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e?.target?.value })
              }
              error={errors?.name}
              required
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="user@example.com"
              value={formData?.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e?.target?.value })
              }
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Role"
              placeholder="Select role"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              error={errors?.role}
              required
            />
            <Select
              label="Department"
              placeholder="Select department"
              options={departmentOptions}
              value={formData?.department}
              onChange={(value) =>
                setFormData({ ...formData, department: value })
              }
            />
          </div>

          <div className="space-y-3 pt-4 border-t border-border">
            <Checkbox
              label="Enable Multi-Factor Authentication (MFA)"
              description="Require additional verification for login security"
              checked={formData?.mfaEnabled}
              onChange={(e) =>
                setFormData({ ...formData, mfaEnabled: e?.target?.checked })
              }
            />
            <Checkbox
              label="Send Welcome Email"
              description="Send account setup instructions to user's email"
              checked={formData?.sendWelcomeEmail}
              onChange={(e) =>
                setFormData({ ...formData, sendWelcomeEmail: e?.target?.checked })
              }
            />
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon
                name="Info"
                size={20}
                className="text-primary flex-shrink-0 mt-0.5"
              />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">
                  Account Setup:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>
                    User will receive a temporary password via email if welcome
                    email is enabled
                  </li>
                  <li>
                    Password must be changed on first login for security
                  </li>
                  <li>
                    Default permissions will be assigned based on selected role
                  </li>
                  <li>Account status will be set to Active by default</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="default" iconName="UserPlus" onClick={handleSubmit}>
            Create User
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;